import React from 'react';
import { Button } from '../ui/button';
import { Loader2, UserCheck, UserX, Users } from 'lucide-react';
import { useAdminEmail } from './AdminEmailContext';

const ManualRecipientsModal = () => {
  const ctx = useAdminEmail();
  const {
    manualRecipients, setManualRecipients,
    manualFlagged, setManualFlagged,
    recipientTab, setRecipientTab,
    flagUser, flaggingUser,
  } = ctx;

  if (!manualRecipients) return null;

  const close = () => { setManualRecipients(null); setManualFlagged(null); };

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal-content recipients-modal tabbed-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Email Recipients</h3>
          <button className="close-btn" onClick={close}>×</button>
        </div>

        <div className="recipient-tabs">
          <button
            className={`recipient-tab ${recipientTab === 'active' ? 'active' : ''}`}
            onClick={() => setRecipientTab('active')}
            data-testid="recipient-tab-active"
          >
            <UserCheck size={16} />
            Active Recipients ({manualRecipients?.total_count || 0})
          </button>
          <button
            className={`recipient-tab ${recipientTab === 'flagged' ? 'active' : ''}`}
            onClick={() => setRecipientTab('flagged')}
            data-testid="recipient-tab-flagged"
          >
            <UserX size={16} />
            Flagged / Test Users ({manualFlagged?.total_count || 0})
          </button>
        </div>

        <div className="modal-body">
          {recipientTab === 'active' && (
            <>
              <p className="recipient-count">
                <strong>{manualRecipients.total_count}</strong> users will receive emails
                {manualFlagged?.total_count > 0 && (
                  <span className="flagged-note"> ({manualFlagged.total_count} excluded)</span>
                )}
              </p>
              <div className="recipients-list">
                {manualRecipients.recipients.length === 0 ? (
                  <div className="empty-recipients">
                    <Users size={32} />
                    <p>No active recipients</p>
                  </div>
                ) : (
                  manualRecipients.recipients.map((r, idx) => (
                    <div key={idx} className="recipient-row" data-testid={`recipient-row-${idx}`}>
                      <div className="recipient-info">
                        <span className="recipient-name">{r.name || 'No name'}</span>
                        <span className="recipient-email">{r.email}</span>
                        {r.username && <span className="recipient-username">@{r.username}</span>}
                      </div>
                      <div className="recipient-actions">
                        <span className={`recipient-status ${r.status}`}>
                          {r.status === 'opted_in' ? 'Opted In' : 'Default'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flag-btn"
                          onClick={() => flagUser(r.user_id, true)}
                          disabled={flaggingUser === r.user_id}
                          title="Flag as test user (exclude from emails)"
                          data-testid={`flag-user-${idx}`}
                        >
                          {flaggingUser === r.user_id ? (
                            <Loader2 size={14} className="spinning" />
                          ) : (
                            <UserX size={14} />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {manualRecipients.total_count > 100 && (
                <p className="more-recipients">...and {manualRecipients.total_count - 100} more</p>
              )}
            </>
          )}

          {recipientTab === 'flagged' && (
            <>
              <p className="recipient-count flagged-header">
                <strong>{manualFlagged?.total_count || 0}</strong> users excluded from emails
              </p>
              <p className="flagged-description">
                These users are flagged as test accounts and won't receive mass emails.
                Auto-detected patterns include: @example.com, @test.com, testuser, etc.
              </p>
              <div className="recipients-list">
                {!manualFlagged?.recipients?.length ? (
                  <div className="empty-recipients">
                    <UserCheck size={32} />
                    <p>No flagged users</p>
                  </div>
                ) : (
                  manualFlagged.recipients.map((r, idx) => (
                    <div key={idx} className="recipient-row flagged-row" data-testid={`flagged-row-${idx}`}>
                      <div className="recipient-info">
                        <span className="recipient-name">{r.name || 'No name'}</span>
                        <span className="recipient-email">{r.email}</span>
                        {r.username && <span className="recipient-username">@{r.username}</span>}
                      </div>
                      <div className="recipient-actions">
                        <span className={`flag-reason ${r.flag_reason}`}>
                          {r.flag_reason === 'manual' ? 'Manual' : 'Auto-detected'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="unflag-btn"
                          onClick={() => flagUser(r.user_id, false)}
                          disabled={flaggingUser === r.user_id}
                          title="Remove flag (include in emails)"
                          data-testid={`unflag-user-${idx}`}
                        >
                          {flaggingUser === r.user_id ? (
                            <Loader2 size={14} className="spinning" />
                          ) : (
                            <UserCheck size={14} />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {manualFlagged?.total_count > 100 && (
                <p className="more-recipients">...and {manualFlagged.total_count - 100} more</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualRecipientsModal;
